import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import SubmitSuccessSplash from '../components/SubmitSuccessSplash';
import EmailValidation from '../components/EmailValidation';
import { Lightbulb } from 'lucide-react';
import PhoneInput from '../components/PhoneInput';
import { submitContactForm } from '../api/contactApi';
import { submitScheduleConsultation } from '../api/scheduleApi';
import { contactInfo } from '../constants/contactInfo';
import { getEmailValidationError, getPhoneValidationError } from '../utils/formValidation';
import { captureReferralFromUrl, getStoredReferralCode } from '../utils/referralSource';
import '../styles/QualifyForm.css';
import '../styles/ScheduleConsult.css';

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  caseType: '',
  // Estate planning
  propertyOutOfGa: '',
  estateSize: '',
  estatePlans: '',
  // Personal injury
  incidentDate: '',
  partiesInvolved: '',
  // Landlord / tenant
  propertyLocation: '',
  disputeDescription: '',
  // Business disputes
  businessDisputeDescription: '',
  // Other / not sure
  description: '',
  // Required consents (live site contact form)
  consentSms: false,
  agreeLegal: false,
};

function buildCaseDescription(data) {
  switch (data.caseType) {
    case 'estate-planning':
      return [
        `Property out of Georgia: ${data.propertyOutOfGa || 'Not answered'}`,
        `Size of estate: ${data.estateSize || 'Not answered'}`,
        `Estate plans / wishes:\n${data.estatePlans || 'Not answered'}`,
      ].join('\n\n');
    case 'personal-injury':
      return [
        `Date of incident: ${data.incidentDate || 'Not answered'}`,
        `Parties involved, location, etc.:\n${data.partiesInvolved || 'Not answered'}`,
      ].join('\n\n');
    case 'landlord-tenant':
      return [
        `Location of property in dispute: ${data.propertyLocation || 'Not answered'}`,
        `Description of dispute:\n${data.disputeDescription || 'Not answered'}`,
      ].join('\n\n');
    case 'business-disputes':
      return data.businessDisputeDescription || '';
    case 'other-not-sure':
    default:
      return data.description || '';
  }
}

function QualifyForm() {
  const navigate = useNavigate();
  const [showScheduleWidget, setShowScheduleWidget] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [contactConfirmation, setContactConfirmation] = useState(null);

  // Persist PIBOOST / campaign ref from URL for this browser session
  useEffect(() => {
    captureReferralFromUrl();
  }, []);

  // Scheduling widget state
  const [currentStep, setCurrentStep] = useState('date');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState({});
  const [scheduleClientInfo, setScheduleClientInfo] = useState({
    name: '',
    email: '',
    phone: '',
    reason: '',
    consentSms: false,
    agreeLegal: false,
  });
  const [scheduleError, setScheduleError] = useState('');
  const [isScheduleSubmitting, setIsScheduleSubmitting] = useState(false);
  const [scheduleSuccess, setScheduleSuccess] = useState(false);
  const [scheduleConfirmation, setScheduleConfirmation] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      if (name === 'caseType') {
        return {
          ...EMPTY_FORM,
          firstName: prev.firstName,
          lastName: prev.lastName,
          email: prev.email,
          phone: prev.phone,
          caseType: value,
          consentSms: prev.consentSms,
          agreeLegal: prev.agreeLegal,
        };
      }
      return { ...prev, [name]: type === 'checkbox' ? checked : value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!formData.consentSms || !formData.agreeLegal) {
      setSubmitError('Please check the required consent and terms boxes before submitting.');
      return;
    }

    const description = buildCaseDescription(formData).trim();
    if (!description) {
      setSubmitError('Please complete the case details for your legal matter.');
      return;
    }

    const payload = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      caseType: formData.caseType.trim(),
      description,
      urgency: 'normal',
      referralCode: getStoredReferralCode(),
      // Consent record for TCPA / legal audit (required checkboxes)
      consentSms: true,
      agreeTerms: true,
      agreePrivacy: true,
      consentedAt: new Date().toISOString(),
    };

    const emailError = getEmailValidationError(payload.email, { required: true });
    if (emailError) {
      setSubmitError(emailError);
      return;
    }

    const phoneError = getPhoneValidationError(payload.phone, { required: true });
    if (phoneError) {
      setSubmitError(phoneError);
      return;
    }

    setIsSubmitting(true);

    try {
      await submitContactForm(payload);
      setContactConfirmation({ email: payload.email });
      setSubmitSuccess(true);
      setFormData(EMPTY_FORM);
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again or call us.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Scheduling widget functions
  const formatSlotDisplay = (hour, minute) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
  };

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 16; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push({ value: time, display: formatSlotDisplay(hour, minute) });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const toLocalDateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const generateCalendar = (month) => {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);
    
    const startDate = new Date(firstDay);
    const dayOfWeek = firstDay.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startDate.setDate(firstDay.getDate() + mondayOffset);
    
    const endDate = new Date(lastDay);
    const lastDayOfWeek = lastDay.getDay();
    const sundayOffset = lastDayOfWeek === 0 ? 0 : 7 - lastDayOfWeek;
    endDate.setDate(lastDay.getDate() + sundayOffset);
    
    const calendar = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const dateObj = {
        date: new Date(currentDate),
        isCurrentMonth: currentDate.getMonth() === monthIndex,
        isPast: currentDate < new Date() || (currentDate.toDateString() === new Date().toDateString()),
        isWeekend: currentDate.getDay() === 0 || currentDate.getDay() === 6
      };
      calendar.push(dateObj);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return calendar;
  };

  const calendar = generateCalendar(currentMonth);

  const isTimeSlotAvailable = (date, time) => {
    const dateStr = date.toISOString().split('T')[0];
    const unavailableSlots = availableSlots[dateStr] || [];
    return !unavailableSlots.includes(time);
  };

  const getAvailableTimeSlots = (date) => {
    if (!date) return [];
    return timeSlots.filter(slot => isTimeSlotAvailable(date, slot.value));
  };

  const handleDateSelect = (dateObj) => {
    if (dateObj.isCurrentMonth && !dateObj.isPast && !dateObj.isWeekend) {
      setSelectedDate(dateObj.date);
      setCurrentStep('time');
    }
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setCurrentStep('info');
  };

  const handleBackToDate = () => {
    setCurrentStep('date');
    setSelectedTime(null);
  };

  const handleBackToTime = () => {
    setCurrentStep('time');
  };

  const handleMonthChange = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const handleScheduleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setScheduleClientInfo((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const emptyScheduleClient = () => ({
    name: '',
    email: '',
    phone: '',
    reason: '',
    consentSms: false,
    agreeLegal: false,
  });

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    setScheduleError('');
    setScheduleSuccess(false);

    if (!selectedDate || !selectedTime || !scheduleClientInfo.name.trim()) {
      setScheduleError('Please fill in all required fields and select a date and time.');
      return;
    }

    const emailError = getEmailValidationError(scheduleClientInfo.email, { required: true });
    if (emailError) {
      setScheduleError(emailError);
      return;
    }

    const phoneError = getPhoneValidationError(scheduleClientInfo.phone, { required: true });
    if (phoneError) {
      setScheduleError(phoneError);
      return;
    }

    if (!scheduleClientInfo.consentSms || !scheduleClientInfo.agreeLegal) {
      setScheduleError(
        'Please consent to SMS messages and agree to the Terms & Conditions and Privacy Policy.'
      );
      return;
    }

    const timeLabel = timeSlots.find((slot) => slot.value === selectedTime)?.display;
    const dateLabel = selectedDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    const confirmEmail = scheduleClientInfo.email.trim();

    setIsScheduleSubmitting(true);

    try {
      await submitScheduleConsultation({
        name: scheduleClientInfo.name.trim(),
        email: confirmEmail,
        phone: scheduleClientInfo.phone.trim(),
        reason: scheduleClientInfo.reason.trim(),
        scheduledDate: toLocalDateString(selectedDate),
        scheduledTime: selectedTime,
        referralCode: getStoredReferralCode(),
        consentSms: true,
        agreeTerms: true,
        agreePrivacy: true,
        consentedAt: new Date().toISOString(),
      });

      setScheduleConfirmation({
        dateLabel,
        timeLabel: `${timeLabel} ET`,
        email: confirmEmail,
      });
      setScheduleSuccess(true);
      setSelectedDate(null);
      setSelectedTime(null);
      setCurrentStep('date');
      setScheduleClientInfo(emptyScheduleClient());
    } catch (err) {
      setScheduleError(err.message || 'Something went wrong. Please try again or call us.');
    } finally {
      setIsScheduleSubmitting(false);
    }
  };

  const handleBackToContact = () => {
    setShowScheduleWidget(false);
    setSelectedDate(null);
    setSelectedTime(null);
    setCurrentStep('date');
    setScheduleClientInfo(emptyScheduleClient());
    setScheduleError('');
    setScheduleSuccess(false);
    setScheduleConfirmation(null);
  };

  const handleGoHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  return (
    <div className={`qualify-form ${showScheduleWidget ? 'scheduler-active' : ''}`}>
      {showScheduleWidget ? (
        <div className="fullscreen-scheduler">
          {scheduleSuccess && scheduleConfirmation ? (
            <SubmitSuccessSplash
              lead="Your consultation has been scheduled. We sent a confirmation to your email."
              details={[
                {
                  label: 'When',
                  value: (
                    <>
                      {scheduleConfirmation.dateLabel}
                      <br />
                      {scheduleConfirmation.timeLabel}
                    </>
                  ),
                },
                {
                  label: 'Confirmation sent to',
                  value: scheduleConfirmation.email,
                },
              ]}
              note="Our team has been notified and will be ready for your appointment."
              onGoHome={handleGoHome}
            />
          ) : (
          <>
          <button className="scheduler-back-btn" onClick={handleBackToContact}>
            ← Back to Contact Form
          </button>
          <div className="scheduling-widget-container">
            <div className="scheduling-widget">
              {/* Step 1: Date Selection */}
              {currentStep === 'date' && (
                <div className="widget-section step-section">
                  <div className="step-header">
                    <h2>Choose a Date</h2>
                  </div>
                  
                  <div className="calendar-container">
                    <div className="calendar-header">
                      <button 
                        className="month-nav-btn"
                        onClick={() => handleMonthChange(-1)}
                      >
                        ‹
                      </button>
                      <h3>{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
                      <button 
                        className="month-nav-btn"
                        onClick={() => handleMonthChange(1)}
                      >
                        ›
                      </button>
                    </div>
                    
                    <div className="calendar-grid">
                      <div className="calendar-weekdays">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                          <div key={day} className="weekday-header">{day}</div>
                        ))}
                      </div>
                      
                      <div className="calendar-dates">
                        {calendar.map((dateObj, index) => (
                          <button
                            key={index}
                            className={`calendar-date ${!dateObj.isCurrentMonth ? 'other-month' : ''} ${dateObj.isPast ? 'past' : ''} ${dateObj.isWeekend ? 'weekend' : ''} ${selectedDate && selectedDate.getTime() === dateObj.date.getTime() ? 'selected' : ''}`}
                            onClick={() => handleDateSelect(dateObj)}
                            disabled={dateObj.isPast || dateObj.isWeekend}
                          >
                            {dateObj.date.getDate()}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Time Selection */}
              {currentStep === 'time' && (
                <div className="widget-section step-section">
                  <div className="step-header">
                    <button className="back-btn" onClick={handleBackToDate}>
                      ← Back to dates
                    </button>
                    <h2>Choose a Time</h2>
                    <div className="selected-date-display">
                      {selectedDate?.toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </div>
                  </div>
                  
                  <div className="time-selection">
                    <div className="time-grid">
                      {getAvailableTimeSlots(selectedDate).map((slot, index) => (
                        <button
                          key={index}
                          className={`time-option ${selectedTime === slot.value ? 'selected' : ''}`}
                          onClick={() => handleTimeSelect(slot.value)}
                        >
                          {slot.display}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Client Information */}
              {currentStep === 'info' && (
                <div className="widget-section step-section">
                  <div className="step-header">
                    <button className="back-btn" onClick={handleBackToTime}>
                      ← Back to times
                    </button>
                    <h2>Your Information</h2>
                    <div className="selected-appointment-display">
                      <div className="appointment-detail">
                        <strong>Date:</strong> {selectedDate?.toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </div>
                      <div className="appointment-detail">
                        <strong>Time:</strong> {timeSlots.find(slot => slot.value === selectedTime)?.display} EST
                      </div>
                    </div>
                  </div>
                  
                  <form onSubmit={handleScheduleSubmit} className="client-form">
                    <div className="form-group">
                      <label htmlFor="schedule-name">Full Name</label>
                      <input
                        type="text"
                        id="schedule-name"
                        name="name"
                        value={scheduleClientInfo.name}
                        onChange={handleScheduleInputChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="schedule-email">Email Address</label>
                      <input
                        type="email"
                        id="schedule-email"
                        name="email"
                        value={scheduleClientInfo.email}
                        onChange={handleScheduleInputChange}
                        required
                      />
                      <EmailValidation email={scheduleClientInfo.email} />
                    </div>

                    <div className="form-group">
                      <label htmlFor="schedule-phone">Phone Number</label>
                      <PhoneInput
                        id="schedule-phone"
                        name="phone"
                        value={scheduleClientInfo.phone}
                        onChange={handleScheduleInputChange}
                        placeholder="(555)-123-4567"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="schedule-reason">Reason for Consultation</label>
                      <textarea
                        id="schedule-reason"
                        name="reason"
                        value={scheduleClientInfo.reason}
                        onChange={handleScheduleInputChange}
                        rows="4"
                        placeholder="Briefly describe your legal matter..."
                      />
                    </div>

                    <div className="form-section form-consent-section">
                      <div className="form-consent">
                        <label className="consent-label" htmlFor="schedule-consentSms">
                          <input
                            type="checkbox"
                            id="schedule-consentSms"
                            name="consentSms"
                            checked={scheduleClientInfo.consentSms}
                            onChange={handleScheduleInputChange}
                            required
                          />
                          <span>
                            I consent to receive transactional messages related to my account, or
                            services I have requested from Jones &amp; Booth. These messages may
                            include appointment reminders, and account notifications, among others.
                            Message frequency may vary. Message &amp; data rates may apply. Text HELP
                            for assistance. You can reply STOP to unsubscribe at any time.
                          </span>
                        </label>
                      </div>
                      <div className="form-consent">
                        <label className="consent-label" htmlFor="schedule-agreeLegal">
                          <input
                            type="checkbox"
                            id="schedule-agreeLegal"
                            name="agreeLegal"
                            checked={scheduleClientInfo.agreeLegal}
                            onChange={handleScheduleInputChange}
                            required
                          />
                          <span>
                            I agree to the{' '}
                            <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                              Terms &amp; Conditions
                            </Link>{' '}
                            and{' '}
                            <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">
                              Privacy Policy
                            </Link>
                            . I understand Jones &amp; Booth may email me about this scheduled
                            consultation.
                          </span>
                        </label>
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="submit-btn"
                      disabled={
                        isScheduleSubmitting
                        || !scheduleClientInfo.name.trim()
                        || !scheduleClientInfo.email
                        || !scheduleClientInfo.phone
                        || !scheduleClientInfo.consentSms
                        || !scheduleClientInfo.agreeLegal
                      }
                    >
                      {isScheduleSubmitting ? 'Scheduling…' : 'Schedule Consultation'}
                    </button>

                    {scheduleError && (
                      <div className="form-error" role="alert">
                        <p>{scheduleError}</p>
                      </div>
                    )}
                  </form>
                </div>
              )}
            </div>

            {/* Appointment Summary */}
            <div className="appointment-summary">
              <h3>Appointment Summary</h3>
              <div className="summary-content">
                <div className="progress-indicator">
                  <div className={`progress-step ${currentStep === 'date' ? 'active' : selectedDate ? 'completed' : ''}`}>
                    <span className="step-number">1</span>
                    <span className="step-text">Choose a day</span>
                  </div>
                  <div className={`progress-step ${currentStep === 'time' ? 'active' : selectedTime ? 'completed' : ''}`}>
                    <span className="step-number">2</span>
                    <span className="step-text">Select a time</span>
                  </div>
                  <div className={`progress-step ${currentStep === 'info' ? 'active' : ''}`}>
                    <span className="step-number">3</span>
                    <span className="step-text">Enter your information</span>
                  </div>
                </div>
                
                {selectedDate && (
                  <div className="summary-item">
                    <strong>Date:</strong> {selectedDate.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                )}
                {selectedTime && (
                  <div className="summary-item">
                    <strong>Time:</strong> {timeSlots.find(slot => slot.value === selectedTime)?.display} EST
                  </div>
                )}
                <div className="summary-item">
                  <strong>Duration:</strong> 30 minutes
                </div>
                <div className="summary-item">
                  <strong>Type:</strong> Initial consultation
                </div>
              </div>
            </div>
          </div>
          </>
          )}
        </div>
      ) : submitSuccess && contactConfirmation ? (
        <div className="fullscreen-scheduler">
          <SubmitSuccessSplash
            lead="We received your consultation request and sent a confirmation to your email. Our team will be in touch soon."
            details={[
              {
                label: 'Confirmation sent to',
                value: contactConfirmation.email,
              },
            ]}
            note="Thank you for reaching out to Jones & Booth Law."
            onGoHome={handleGoHome}
          />
        </div>
      ) : (
        <div className="form-container">
          <div className="contact-header">
            <div className="contact-title">
              <h1>Contact Us</h1>
              <p>Fill out this form to receive a free consultation so we can help with your legal matter.</p>
              <p>We will contact you to assist!</p>
              <div className="contact-office-details">
                <p>
                  {contactInfo.addressLine1}
                  <br />
                  {contactInfo.addressLine2}
                </p>
                <p>
                  Tel: <a href={contactInfo.phoneHref}>{contactInfo.phone}</a>
                </p>
                <p>
                  <a href={contactInfo.emailHref}>{contactInfo.email}</a>
                </p>
              </div>
            </div>

            <div className="schedule-cta">
              <div className="cta-content">
                <h3>Prefer to Schedule?</h3>
                <p>Book a consultation directly with our legal team</p>
                <button 
                  className="schedule-btn"
                  onClick={() => {
                    setShowScheduleWidget(true);
                    setScheduleError('');
                    setScheduleSuccess(false);
                    setScheduleConfirmation(null);
                  }}
                >
                  + Schedule Consultation
                </button>
              </div>
            </div>
          </div>

        
        <form onSubmit={handleSubmit} className="qualify-form-content">
          <div className="form-section">
            <h3>Personal Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Enter your first name"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Enter your last name"
                  required
                />
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h3>Contact Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  required
                />
                <EmailValidation email={formData.email} />
              </div>
              
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <PhoneInput
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555)-123-4567"
                  required
                />
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h3>Legal Matter Details</h3>
            <div className="form-group">
              <label htmlFor="caseType">Type of Legal Matter</label>
              <select
                id="caseType"
                name="caseType"
                value={formData.caseType}
                onChange={handleChange}
                required
              >
                <option value="">Select a practice area</option>
                <option value="personal-injury">Personal Injury</option>
                <option value="estate-planning">Estate Planning</option>
                <option value="landlord-tenant">Landlord / Tenant</option>
                <option value="business-disputes">Business Disputes</option>
                <option value="other-not-sure">Other / Not Sure</option>
              </select>
            </div>
          </div>

          {formData.caseType && (
            <div className="form-section">
              <h3>Case Details</h3>

              {formData.caseType === 'estate-planning' && (
                <>
                  <div className="form-group">
                    <label htmlFor="propertyOutOfGa">Property out of Georgia?</label>
                    <select
                      id="propertyOutOfGa"
                      name="propertyOutOfGa"
                      value={formData.propertyOutOfGa}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="estateSize">Size of estate (approximate value)</label>
                    <input
                      type="text"
                      id="estateSize"
                      name="estateSize"
                      value={formData.estateSize}
                      onChange={handleChange}
                      placeholder="e.g. $500,000"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="estatePlans">Have you decided what you want to do with your estate?</label>
                    <textarea
                      id="estatePlans"
                      name="estatePlans"
                      value={formData.estatePlans}
                      onChange={handleChange}
                      rows="4"
                      placeholder="Share your goals, known wishes, or questions about your estate..."
                      required
                    />
                  </div>
                </>
              )}

              {formData.caseType === 'personal-injury' && (
                <>
                  <div className="form-group">
                    <label htmlFor="incidentDate">Date of incident</label>
                    <input
                      type="date"
                      id="incidentDate"
                      name="incidentDate"
                      value={formData.incidentDate}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="partiesInvolved">Parties involved, location, etc.</label>
                    <textarea
                      id="partiesInvolved"
                      name="partiesInvolved"
                      value={formData.partiesInvolved}
                      onChange={handleChange}
                      rows="4"
                      placeholder="Who was involved, where it happened, and any other details..."
                      required
                    />
                  </div>
                </>
              )}

              {formData.caseType === 'landlord-tenant' && (
                <>
                  <div className="form-group">
                    <label htmlFor="propertyLocation">Location of property in dispute</label>
                    <input
                      type="text"
                      id="propertyLocation"
                      name="propertyLocation"
                      value={formData.propertyLocation}
                      onChange={handleChange}
                      placeholder="City, county, or address"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="disputeDescription">Description of dispute</label>
                    <textarea
                      id="disputeDescription"
                      name="disputeDescription"
                      value={formData.disputeDescription}
                      onChange={handleChange}
                      rows="4"
                      placeholder="Describe the landlord/tenant dispute..."
                      required
                    />
                  </div>
                </>
              )}

              {formData.caseType === 'business-disputes' && (
                <div className="form-group">
                  <label htmlFor="businessDisputeDescription">Describe the business dispute</label>
                  <textarea
                    id="businessDisputeDescription"
                    name="businessDisputeDescription"
                    value={formData.businessDisputeDescription}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Describe the business dispute..."
                    required
                  />
                </div>
              )}

              {formData.caseType === 'other-not-sure' && (
                <div className="form-group">
                  <label htmlFor="description">Brief Description of Your Legal Matter</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Please provide a detailed description of your legal issue..."
                    required
                  />
                </div>
              )}

              <div className="form-help">
                <p>
                  <Lightbulb size={16} strokeWidth={1.75} className="form-help-icon" aria-hidden="true" />
                  <strong>Tip:</strong> The more details you provide, the better we can assess your case and provide appropriate legal guidance.
                </p>
              </div>
            </div>
          )}

          <div className="form-section form-consent-section">
            <div className="form-consent">
              <label className="consent-label" htmlFor="consentSms">
                <input
                  type="checkbox"
                  id="consentSms"
                  name="consentSms"
                  checked={formData.consentSms}
                  onChange={handleChange}
                  required
                />
                <span>
                  I consent to receive transactional messages related to my account, or services I have
                  requested from Jones &amp; Booth. These messages may include appointment reminders,
                  and account notifications, among others. Message frequency may vary. Message &amp;
                  data rates may apply. Text HELP for assistance. You can reply STOP to unsubscribe at
                  any time.
                </span>
              </label>
            </div>
            <div className="form-consent">
              <label className="consent-label" htmlFor="agreeLegal">
                <input
                  type="checkbox"
                  id="agreeLegal"
                  name="agreeLegal"
                  checked={formData.agreeLegal}
                  onChange={handleChange}
                  required
                />
                <span>
                  I agree to the{' '}
                  <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                    Terms &amp; Conditions
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </Link>
                </span>
              </label>
            </div>
          </div>
          
          {submitError && (
            <div className="form-error" role="alert">
              <p>{submitError}</p>
            </div>
          )}

          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting…' : 'Submit for Free Consultation'}
          </Button>
        </form>
        </div>
      )}
    </div>
  );
}

export default QualifyForm;
